import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Search, Car } from "lucide-react";

const LOCATIONS = [
  "East Legon (HQ)",
  "Kotoka International Airport",
  "Accra Mall",
  "Cantonments",
  "Tema",
  "Kumasi",
  "Takoradi",
];

const CATEGORIES = [
  { value: "all", label: "All categories" },
  { value: "sedan", label: "Sedan" },
  { value: "compact_suv", label: "Mid-size SUV" },
  { value: "full_suv", label: "Full-size SUV" },
];

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export default function BookingSearchWidget({ compact = false }) {
  const nav = useNavigate();
  const [pickup, setPickup] = useState(LOCATIONS[0]);
  const [dropoff, setDropoff] = useState(LOCATIONS[0]);
  const [pickupDate, setPickupDate] = useState(todayISO(1));
  const [returnDate, setReturnDate] = useState(todayISO(4));
  const [category, setCategory] = useState("all");

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams({
      pickup,
      dropoff,
      pickup_date: pickupDate,
      return_date: returnDate,
      category,
    });
    nav(`/fleet?${params.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      className={`booking-widget bg-white border border-gray-200 ${compact ? "p-4" : "p-6 md:p-7"}`}
      data-testid="booking-search-widget"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 bg-[#e63946] flex items-center justify-center">
          <Car size={15} className="text-white" />
        </div>
        <span className="font-display text-lg font-semibold text-[#0d2040]">Find your ride</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        <div className="md:col-span-3">
          <label className="titan-label">Pick-up location</label>
          <div className="relative">
            <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <select
              value={pickup}
              onChange={(e) => setPickup(e.target.value)}
              className="titan-input pl-8"
              data-testid="booking-pickup-location"
            >
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="md:col-span-3">
          <label className="titan-label">Drop-off location</label>
          <div className="relative">
            <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <select
              value={dropoff}
              onChange={(e) => setDropoff(e.target.value)}
              className="titan-input pl-8"
              data-testid="booking-dropoff-location"
            >
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="titan-label">Pick-up date</label>
          <div className="relative">
            <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="date"
              value={pickupDate}
              min={todayISO()}
              onChange={(e) => setPickupDate(e.target.value)}
              className="titan-input pl-8"
              data-testid="booking-pickup-date"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="titan-label">Return date</label>
          <div className="relative">
            <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="date"
              value={returnDate}
              min={pickupDate}
              onChange={(e) => setReturnDate(e.target.value)}
              className="titan-input pl-8"
              data-testid="booking-return-date"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="titan-label">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="titan-input"
            data-testid="booking-category"
          >
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          Minimum rental: 1 day · Valid driver&apos;s license required · Pickup from East Legon HQ free
        </p>
        <button type="submit" className="btn-primary w-full md:w-auto" data-testid="booking-search-submit">
          <Search size={15} /> Search cars
        </button>
      </div>
    </form>
  );
}
